import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Magnetic, scrollToId } from "@/components/motion";
import { Menu, X } from "lucide-react";

const ANCHORS = [
  { label: "Approach", href: "#approach" },
  { label: "Positioning", href: "#positioning" },
  { label: "The Standard", href: "#standard" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    scrollToId(href);
  };

  return (
    <header data-testid="site-header" className="fixed top-0 left-0 right-0 z-50">
      <nav
        data-testid="main-nav"
        className={`transition-colors duration-500 ${
          scrolled || menuOpen ? "bg-[#0A0A0A] border-b border-[#27272A]" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between gap-3">
          <button
            data-testid="nav-logo"
            onClick={() => {
              setMenuOpen(false);
              window.__lenis ? window.__lenis.scrollTo(0) : window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="font-display text-base sm:text-xl font-bold tracking-tighter text-[#F8F8F5] whitespace-nowrap"
          >
            Barrett Career <span className="text-[#DFFF00]">Brand</span>
          </button>

          <div className="hidden md:flex items-center gap-8">
            {ANCHORS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={(e) => go(e, l.href)}
                className="font-body text-sm text-[#A1A1AA] hover:text-[#F8F8F5] transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/services"
              data-testid="nav-link-services"
              className="font-body text-sm text-[#A1A1AA] hover:text-[#F8F8F5] transition-colors duration-300"
            >
              Services
            </Link>
            <Link
              to="/insights"
              data-testid="nav-link-insights"
              className="font-body text-sm text-[#A1A1AA] hover:text-[#F8F8F5] transition-colors duration-300"
            >
              Insights
            </Link>
            <a
              href="#faqs"
              data-testid="nav-link-faqs"
              onClick={(e) => go(e, "#faqs")}
              className="font-body text-sm text-[#A1A1AA] hover:text-[#F8F8F5] transition-colors duration-300"
            >
              FAQs
            </a>
          </div>

          <div className="flex items-center gap-2">
            <Magnetic
              data-testid="nav-cta"
              onClick={(e) => go(e, "#cv-review")}
              className="rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold text-sm px-4 sm:px-6 py-2.5 hover:bg-[#C8E600] transition-colors duration-300 whitespace-nowrap shrink-0"
            >
              <span className="hidden sm:inline">Free CV Career Brand Score</span>
              <span className="sm:hidden">Free CV Score</span>
            </Magnetic>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              className="md:hidden w-10 h-10 flex items-center justify-center text-[#F8F8F5] hover:text-[#DFFF00] transition-colors shrink-0"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {menuOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden bg-[#0A0A0A] border-t border-[#27272A] px-6 py-4"
          >
            <div className="flex flex-col divide-y divide-[#27272A]/60">
              {ANCHORS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  onClick={(e) => go(e, l.href)}
                  className="text-left py-4 font-body text-base text-[#F8F8F5] hover:text-[#DFFF00] transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <Link
                to="/services"
                data-testid="mobile-nav-link-services"
                onClick={() => setMenuOpen(false)}
                className="py-4 font-body text-base text-[#F8F8F5] hover:text-[#DFFF00] transition-colors"
              >
                Services
              </Link>
              <Link
                to="/insights"
                data-testid="mobile-nav-link-insights"
                onClick={() => setMenuOpen(false)}
                className="py-4 font-body text-base text-[#F8F8F5] hover:text-[#DFFF00] transition-colors"
              >
                Insights
              </Link>
              <a
                href="#faqs"
                data-testid="mobile-nav-link-faqs"
                onClick={(e) => go(e, "#faqs")}
                className="text-left py-4 font-body text-base text-[#F8F8F5] hover:text-[#DFFF00] transition-colors"
              >
                FAQs
              </a>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
